import { areEquivalent } from '@/platform/extensions';
import { ConvergingTask } from '../platform/ConvergingTask';
import { Routing } from '../router/Routing';
import { EmbeddedStateFactory } from './EmbeddedStateFactory';
import { UrlNavigation } from './UrlNavigation';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { NavigationReconfirmationInitiatedEvent } from './events/NavigationReconfirmationInitiatedEvent';

export class UrlRouteProjection extends ConvergingTask<boolean> {
	private readonly navigation: UrlNavigation;
	private readonly routing: Routing;
	private readonly embeddedState: EmbeddedStateFactory;
	private readonly eventAggregationService: EventAggregationService;

	public constructor(
		routing: Routing,
		embeddedState: EmbeddedStateFactory,
		eventAggregationService: EventAggregationService
	) {
		super();

		this.eventAggregationService = eventAggregationService;

		this.navigation = new UrlNavigation(routing, eventAggregationService);
		this.routing = routing;
		this.embeddedState = embeddedState;
	}

	public async project(): Promise<void> {
		await this.execute(null);
	}

	protected override beforeEachRun(runPromise: Promise<unknown>): void {
		this.eventAggregationService.publish(new NavigationReconfirmationInitiatedEvent(runPromise));
	}

	protected override async run(): Promise<boolean> {
		const navigationTargetCurrent = this.routing.currentRoute.value;

		const embeddedStateCommitted = this.embeddedState.fromCommitted();
		const navigationTargetFromCommitted = await this.routing.toNavigationTarget(embeddedStateCommitted);

		const sameName = navigationTargetCurrent.name === navigationTargetFromCommitted?.name;
		const sameParams = areEquivalent(navigationTargetCurrent.params, navigationTargetFromCommitted?.params);
		const sameQuery = areEquivalent(navigationTargetCurrent.query, navigationTargetFromCommitted?.query);

		// Disable URL rewrite for (Prerender) bot requests
		if (navigationTargetCurrent.query.bot === 'true') {
			return false;
		}

		return !sameName || !sameParams || !sameQuery;
	}

	protected override async commit(result: boolean): Promise<void> {
		if (result) {
			await this.navigation.navigateFrom(this.embeddedState.fromCommitted());
		}
	}

	protected override queued(): boolean {
		return false;
	}
}
